import { API_URL, fetchWithErrorHandling, fetchWithAuth } from '../utils/api';
import { normalizeUserDataTextFields } from '../utils/textNormalization';

export interface LoginData {
  // Support both email and nickname for login (like mobile app)
  email?: string;
  emailOrNickname?: string;
  password: string;
}

export interface SignupData {
  nickname: string;
  email: string;
  password: string;
  subscribed: boolean;
  phone: string;
  phoneVerificationToken: string;
  fullname: string;
}

export interface PhoneCallVerification {
  verificationId: string;
  /** Cooldown before another paid verification call may be requested, in seconds. */
  retryAfter: number;
}

export interface VerifiedPhone {
  verificationToken: string;
  /** Remaining lifetime of the registration proof, in seconds. */
  expiresIn: number;
}

export interface UserData {
  _id: string;
  nickname: string;
  email: string;
  coins: number;
  status: number;
  avatar?: string;
  subscribed?: boolean;
  fullname?: string;
  phone?: string;
  vkUserId?: string;
  googleUserId?: string;
  appleUserId?: string;
  telegramUserId?: string;
}

export interface AuthResponse {
  token: string;
  user: UserData;
}

export const normalizeUserData = (
  rawUser: any,
  fallback?: Partial<UserData> | null,
): UserData => {
  const source = rawUser?.userData || rawUser?.user || rawUser || {};

  return normalizeUserDataTextFields({
    _id: String(source._id || source.userId || fallback?._id || ""),
    nickname: source.nickname ?? fallback?.nickname ?? "",
    email: source.email ?? fallback?.email ?? "",
    coins: source.coins ?? fallback?.coins ?? 0,
    status: source.status ?? fallback?.status ?? 0,
    avatar: source.avatar ?? fallback?.avatar,
    subscribed: source.subscribed ?? fallback?.subscribed,
    fullname: source.fullname ?? fallback?.fullname,
    phone: source.phone ?? fallback?.phone,
    vkUserId: source.vkUserId ?? fallback?.vkUserId,
    googleUserId: source.googleUserId ?? fallback?.googleUserId,
    appleUserId: source.appleUserId ?? fallback?.appleUserId,
    telegramUserId: source.telegramUserId ?? fallback?.telegramUserId,
  });
};

export const loginUser = async (loginData: LoginData): Promise<AuthResponse> => {
  const response = await fetchWithErrorHandling(`${API_URL}/auth/login`, {
    method: 'POST',
    body: JSON.stringify(loginData),
  });
  
  // The server returns user data directly in the response, not nested under 'user'
  if (response.token && response.nickname) {
    try {
      const userData = normalizeUserData(response);
      
      localStorage.setItem('authToken', response.token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      return { token: response.token, user: userData };
    } catch (error) {
      console.error('Error storing auth data:', error);
      throw new Error('Failed to store authentication data');
    }
  } else {
    console.error('Invalid response structure:', response);
    throw new Error('Invalid response from server');
  }
};

export const requestPhoneVerificationCall = async (
  phone: string,
): Promise<PhoneCallVerification> => {
  const response = await fetchWithErrorHandling(`${API_URL}/auth/phone/request-call`, {
    method: 'POST',
    body: JSON.stringify({ phone }),
  });

  if (
    !response ||
    typeof response.verificationId !== 'string' ||
    !response.verificationId ||
    typeof response.retryAfter !== 'number'
  ) {
    throw new Error('Invalid phone verification response from server');
  }

  return {
    verificationId: response.verificationId,
    retryAfter: Math.max(0, Math.ceil(response.retryAfter)),
  };
};

export const verifyPhoneNumber = async (
  phone: string,
  code: string,
  verificationId: string,
): Promise<VerifiedPhone> => {
  const response = await fetchWithErrorHandling(`${API_URL}/auth/phone/verify`, {
    method: 'POST',
    body: JSON.stringify({ phone, code, verificationId }),
  });

  if (
    !response ||
    typeof response.verificationToken !== 'string' ||
    !response.verificationToken ||
    typeof response.expiresIn !== 'number' ||
    !Number.isFinite(response.expiresIn) ||
    response.expiresIn <= 0
  ) {
    throw new Error('Invalid phone verification response from server');
  }

  return {
    verificationToken: response.verificationToken,
    expiresIn: Math.max(1, Math.ceil(response.expiresIn)),
  };
};

export const signupUser = async (signupData: SignupData): Promise<AuthResponse> => {
  // The registration contract requires a non-empty full name.
  const cleanSignupData = {
    ...signupData,
    fullname: signupData.fullname.trim(),
  };

  const response = await fetchWithErrorHandling(`${API_URL}/auth/register`, {
    method: 'POST',
    body: JSON.stringify(cleanSignupData),
  });

  // The server returns user data directly in the response, not nested under 'user'
  if (response.token && response.nickname) {
    try {
      const userData = normalizeUserData(response);

      localStorage.setItem('authToken', response.token);
      localStorage.setItem('user', JSON.stringify(userData));

      return { token: response.token, user: userData };
    } catch (error) {
      console.error('Error storing auth data:', error);
      throw new Error('Failed to store authentication data');
    }
  } else {
    console.error('Invalid response structure:', response);
    throw new Error('Invalid response from server');
  }
};

export const telegramLoginIfLinked = async (
  initData: string,
): Promise<AuthResponse | null> => {
  const response = await fetchWithErrorHandling(`${API_URL}/auth/telegram/mobile`, {
    method: 'POST',
    body: JSON.stringify({ initData, loginOnly: true }),
  });

  // Server returns { linked: false } when this Telegram id isn't tied to an account.
  if (response && response.linked === false) {
    return null;
  }

  if (response.token && response.nickname) {
    try {
      const userData = normalizeUserData(response);
      localStorage.setItem('authToken', response.token);
      localStorage.setItem('user', JSON.stringify(userData));
      return { token: response.token, user: userData };
    } catch (error) {
      console.error('Error storing Telegram auth data:', error);
      throw new Error('Failed to store authentication data');
    }
  }
  console.error('Invalid Telegram auth response structure:', response);
  throw new Error('Invalid response from server');
};

export const linkTelegram = async (
  initData: string,
): Promise<AuthResponse> => {
  const response = await fetchWithAuth(`${API_URL}/auth/telegram/link`, {
    method: 'POST',
    body: JSON.stringify({ initData }),
  });

  if (response.token && response.nickname) {
    try {
      const userData = normalizeUserData(response);

      localStorage.setItem('authToken', response.token);
      localStorage.setItem('user', JSON.stringify(userData));

      return { token: response.token, user: userData };
    } catch (error) {
      console.error('Error storing linked auth data:', error);
      throw new Error('Failed to store authentication data');
    }
  } else {
    console.error('Invalid Telegram link response structure:', response);
    throw new Error('Invalid response from server');
  }
};

export const logoutUser = (): void => {
  try {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  } catch (error) {
    console.error('Error clearing auth data:', error);
  }
};

export const clearAuthData = (): void => {
  try {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  } catch (error) {
    console.error('Error clearing auth data:', error);
  }
};

export const getCurrentUser = (): UserData | null => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr || userStr === 'undefined') {
      return null;
    }
    const userData = normalizeUserData(JSON.parse(userStr));
    localStorage.setItem('user', JSON.stringify(userData));
    return userData;
  } catch (error) {
    console.error('Error parsing user data from localStorage:', error);
    // Clear corrupted data
    localStorage.removeItem('user');
    return null;
  }
};

export const getAuthToken = (): string | null => {
  const token = localStorage.getItem('authToken');
  return token && token !== 'undefined' ? token : null;
};

export const isAuthenticated = (): boolean => {
  const token = getAuthToken();
  const user = getCurrentUser();
  return !!(token && user);
};
